import { useEffect, useState } from 'react';
import { copyToClipboard, downloadImage } from '../../lib/clipboard';
import { qrDataUrl } from '../../lib/qr';
import { showToast } from '../Toast';
import type { NpsLink } from '../../api/nps';

/**
 * One shareable NPS link for an audience: the URL with copy-to-clipboard, a QR
 * code (shown on demand, downloadable), and a rotate control that invalidates
 * the old link. The public URL is composed here from the current origin so it's
 * correct in dev and prod alike.
 */
export default function ShareLinkCard({
  link,
  onRotate,
  rotating,
}: {
  link: NpsLink;
  onRotate: () => void;
  rotating: boolean;
}) {
  const url = link.token ? `${window.location.origin}/nps/r/${link.token}` : '';
  // QR is shown by default in the franchisee view; the button collapses it.
  const [showQr, setShowQr] = useState(true);
  const [qr, setQr] = useState<string | null>(null);

  useEffect(() => {
    setQr(null);
    if (showQr && url) qrDataUrl(url).then(setQr).catch(() => setQr(null));
  }, [showQr, url]);

  async function handleCopy() {
    if (await copyToClipboard(url)) showToast('Link copied', { variant: 'success' });
    else showToast('Could not copy link', { variant: 'error' });
  }

  return (
    <div className="nps-share-card">
      <div className="nps-share-head">
        <h3>{link.label}</h3>
        <button type="button" className="nps-link-btn" onClick={onRotate} disabled={rotating}
                title="Generate a new link and QR; the old ones stop working">
          {rotating ? 'Rotating…' : 'Rotate link'}
        </button>
      </div>

      <div className="nps-share-url">
        <input readOnly value={url} onFocus={(e) => e.currentTarget.select()} />
        <button type="button" className="nps-btn" onClick={handleCopy}>Copy</button>
      </div>

      <button type="button" className="nps-link-btn" onClick={() => setShowQr((v) => !v)}>
        {showQr ? 'Hide QR code' : 'Show QR code'}
      </button>

      {showQr && (
        <div className="nps-qr">
          {qr ? (
            <>
              <img src={qr} alt={`QR code for the ${link.label} survey link`} width={180} height={180} />
              <button
                type="button"
                className="nps-btn"
                onClick={() => downloadImage(qr, `nps-${link.audience}-qr.png`)}
              >
                Download QR
              </button>
            </>
          ) : (
            <span className="nps-respond-muted">Generating…</span>
          )}
        </div>
      )}
    </div>
  );
}
